import { InputLabel } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import {
  HandGestureIcon,
  ISelectValue,
  Select,
  SelectArrowDownIcon,
  TextInput,
} from '@room-match/shared-ui-components';
import EthnicAppearances from 'data/EthnicAppearances.json';
import eyeColour from 'data/EyeColor.json';
import hairColour from 'data/HairColor.json';
import hairLength from 'data/HairLength.json';
import months from 'data/Months.json';
import moment from 'moment';
import AdultSlider from 'pages/MyInfo/GeneralAppearance/AdultSlider';
import MinorSlider from 'pages/MyInfo/GeneralAppearance/MinorSlider';
import React, { useEffect, useState } from 'react';
import { COLORS } from 'shared/constants/COLORS';
import { IGeneralSection } from 'shared/interfaces/IGeneral';
import { ITalentUpdatePayload } from 'shared/interfaces/ITalent';
import { useAppSelector } from 'shared/redux/store';
import { talentService } from 'shared/services/talentService';
import { getMinorYears } from 'shared/utils/common';
import CompCardButton from './CompCardButton';
import { DoBContainer, Heading, InstructionCont, OpportunitiesCont, ThreeColumn, useStyles } from './SizeCard.styles';

interface Props {
  handleAutocompleteChange: (key: string, value: string | null) => void;
  generalInfo: IGeneralSection;
  setmeInfo: React.Dispatch<React.SetStateAction<ITalentUpdatePayload>>;
  birth: { day: string; month: string; year: string };
  setBirth: React.Dispatch<React.SetStateAction<{ day: string; month: string; year: string }>>;
  val: number[] | null;
  setVal: React.Dispatch<React.SetStateAction<number[] | null>>;
  compCardAdd?: (val: { key: string; value: string }) => void;
  compCardRemove?: (val: { key: string }) => void;
}
const { updateMe } = talentService();

const GeneralOnSizeCard: React.FC<Props> = ({
  handleAutocompleteChange,
  generalInfo,
  setmeInfo,
  birth,
  setBirth,
  val,
  setVal,
  compCardAdd,
}) => {
  const classes = useStyles();
  const { user: userState } = useAppSelector((state) => state);
  const generateDays = (n: number) => {
    const days = [...Array(n).keys()];
    const arr: ISelectValue[] = [];
    days.forEach((e) => arr.push({ key: `${e + 1}`, value: `${e + 1}` }));
    return arr;
  };

  const [days, setDays] = useState(generateDays(31));

  const getAllowance = (age: number) => {
    if (age >= 3 && age <= 5) {
      return 1;
    } else if (age >= 6 && age <= 10) {
      return 2;
    }
    return 3;
  };

  const is31 = (month: string) => {
    return (
      month === '01' ||
      month === '03' ||
      month === '05' ||
      month === '07' ||
      month === '08' ||
      month === '10' ||
      month === '12'
    );
  };

  const birthChange = (e: any) => {
    const newBirth = { ...birth, [e.target.name]: e.target.value };
    const birth_date: any = `${
      newBirth.day.length === 1
        ? `0${newBirth.day}`
        : !is31(newBirth.month) && parseInt(newBirth.day) > 30
          ? newBirth.month === '02'
            ? '28'
            : '30'
          : newBirth.day
    }/${newBirth.month}/${newBirth.year}`;

    const now = moment(new Date());
    const birthMoment = moment(`${birth.month}/${birth.day}/${birth.year}`);
    const newBirthMoment = moment(
      `${newBirth.month}/${
        newBirth.day.length === 1
          ? `0${newBirth.day}`
          : !is31(newBirth.month) && parseInt(newBirth.day) > 30
            ? newBirth.month === '02'
              ? '28'
              : '30'
            : newBirth.day
      }/${newBirth.year}`,
    );
    const duration1 = now.diff(birthMoment, 'year');
    const duration2 = now.diff(newBirthMoment, 'year');

    //if year has changes update val
    if (duration1 !== duration2) {
      const allowance = getAllowance(duration1);

      const min = duration2 - allowance;
      const max = duration2 + allowance;

      if (allowance === 1) {
        setVal([min, max]);
      } else {
        setVal([min + 1, max - 1]);
      }

      setmeInfo((i) => ({
        ...i,
        birth_date,
        age_range_from: allowance === 1 ? min : min + 1,
        age_range_to: allowance === 1 ? max : max - 1,
      }));
    } else {
      setmeInfo((i) => ({ ...i, birth_date }));
    }
    setBirth((b) => ({ ...b, [e.target.name]: e.target.value }));
  };

  const minorDefault = () => {
    const me = userState?.user?.attributes;
    if (me?.adult_minor === 'adult') return;
    const diff = new Date().getFullYear() - parseInt(me?.birth_date?.split('/')[2] || '0');
    const allowance = getAllowance(diff);

    const min = diff - allowance;
    const max = diff + allowance;
    let merawdata: any = {};

    if (allowance === 1) {
      setVal([min, max]);
      merawdata = {
        age_range_from: min,
        age_range_to: max,
      };
    } else {
      setVal([min + 1, max - 1]);
      merawdata = {
        age_range_from: min + 1,
        age_range_to: max - 1,
      };
    }
  };

  useEffect(() => {
    //checking leap year
    if (birth.month === '02') {
      if (parseInt(birth.year) % 4 === 0) {
        if (parseInt(birth.day) > 29) {
          setBirth((b) => ({ ...b, day: '29' }));
        }
        setDays(generateDays(29));
      } else {
        if (parseInt(birth.day) > 28) {
          setBirth((b) => ({ ...b, day: '28' }));
        }
        setDays(generateDays(28));
      }
    } else {
      if (is31(birth.month)) {
        setDays(generateDays(31));
      } else {
        if (birth.day === '31') {
          setBirth((b) => ({ ...b, day: '30' }));
        }
        setDays(generateDays(30));
      }
    }
  }, [birth]);

  useEffect(() => {
    const me = userState?.user?.attributes;

    if (!me) return;
    const { gender, identifies_as, adult_minor, birth_date, age_range_from, age_range_to } = me;

    setmeInfo((i) => ({ ...i, gender, identifies_as }));
    let merawdata: any = {
      gender,
      identifies_as,
    };

    if (me.birth_date) {
      const birthSplit = me.birth_date?.split('/');
      setBirth({
        day: parseInt(birthSplit[0]) > 9 ? birthSplit[0] : birthSplit[0].substring(1),
        month: birthSplit[1],
        year: birthSplit[2],
      });
    }

    if (adult_minor === 'adult') {
      if (age_range_from && age_range_to && age_range_from > 15 && age_range_to < 110) {
        setVal([age_range_from, age_range_to]);
        merawdata = {
          ...merawdata,
          age_range_from,
          age_range_to,
        };
      } else {
        const min = 15;
        const max = 23;
        merawdata = {
          ...merawdata,
          age_range_from: min,
          age_range_to: max,
        };
        setVal([min, max]);
        // }
      }
    } else {
      if (age_range_from && age_range_to) {
        setVal([age_range_from, age_range_to]);
        merawdata = {
          ...merawdata,
          age_range_from,
          age_range_to,
        };
      } else {
        minorDefault();
      }
    }
  }, [userState]);

  return (
    <OpportunitiesCont>
      <p style={{ fontSize: 25 }}>General Appearance</p>
      {!compCardAdd && <Heading>General</Heading>}

      {compCardAdd ? (
        <React.Fragment />
      ) : userState?.user && userState?.user.attributes.adult_minor === 'adult' ? (
        <>
          <InputLabel id="dateOfBirth" style={{ color: '#98a1ac' }}>
            Date of Birth
          </InputLabel>
          <DoBContainer>
            <Select
              onChange={birthChange}
              value={birth.day}
              variant="outlined"
              className={classes.select}
              fullWidth
              data={days}
              // IconComponent={SelectArrowDownIcon}
              placeHolder={'Select Day'}
              name="day"
            />
            <Select
              onChange={birthChange}
              value={birth.month}
              variant="outlined"
              className={classes.select}
              fullWidth
              data={months}
              // IconComponent={SelectArrowDownIcon}
              placeHolder={'Select Month'}
              name="month"
            />
            <Select
              onChange={birthChange}
              value={birth.year}
              variant="outlined"
              className={classes.select}
              fullWidth
              data={getMinorYears(101, 18)}
              // IconComponent={SelectArrowDownIcon}
              placeHolder={'Select Year'}
              name="year"
            />
          </DoBContainer>
          <InstructionCont>
            <HandGestureIcon fill={COLORS.PRIMARY} />
            <p style={{ marginLeft: 20, wordBreak: 'break-word', fontSize: 16 }}>
              Drag the slider to your desired playing age range, then adjust the minimum and the maximum to suit.
            </p>
          </InstructionCont>
          {val ? <AdultSlider setVal={setVal} val={val} /> : <React.Fragment />}
        </>
      ) : (
        <>
          <InputLabel id="dateOfBirth" style={{ color: '#98a1ac' }}>
            Date of Birth
          </InputLabel>
          <DoBContainer>
            <Select
              onChange={birthChange}
              value={birth.day}
              variant="outlined"
              className={classes.select}
              fullWidth
              data={days}
              IconComponent={SelectArrowDownIcon}
              placeHolder={'Select Day'}
              name="day"
            />
            <Select
              onChange={birthChange}
              value={birth.month}
              variant="outlined"
              className={classes.select}
              fullWidth
              data={months}
              IconComponent={SelectArrowDownIcon}
              placeHolder={'Select Month'}
              name="month"
            />
            <Select
              onChange={birthChange}
              value={birth.year}
              variant="outlined"
              className={classes.select}
              fullWidth
              data={getMinorYears(19)}
              IconComponent={SelectArrowDownIcon}
              placeHolder={'Select Year'}
              name="year"
            />
          </DoBContainer>
          {/* <AdultSlider age={age} /> */}
          <MinorSlider birth={birth} val={val} setVal={setVal} />
        </>
      )}
      {<Heading>Appearance</Heading>}
      <ThreeColumn isModal={1}>
        {!compCardAdd && (
          <div>
            <InputLabel id="primaryEthnicAppearance" style={{ color: '#98a1ac' }}>
              Ethnic Appearance (Primary)
            </InputLabel>
            <Autocomplete
              onChange={(e, newValue) => {
                handleAutocompleteChange('ethnic_appearance_primary', newValue);
              }}
              value={generalInfo.ethnic_appearance_primary}
              options={EthnicAppearances.map((i) => i.value)}
              getOptionSelected={(option, value) => option === value}
              fullWidth
              classes={{
                option: classes.option,
              }}
              renderInput={(params) => (
                <TextInput
                  placeholder={'Select'}
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    classes: {
                      root: classes.textInputStyle,
                    },
                  }}
                />
              )}
              disableClearable
            />
          </div>
        )}
        {!compCardAdd && (
          <div>
            <InputLabel id="secondaryEthnicAppearance" style={{ color: '#98a1ac' }}>
              Ethnic Appearance (Secondary)
            </InputLabel>
            <Autocomplete
              onChange={(e, newValue) => {
                handleAutocompleteChange('ethnic_appearance_secondary', newValue);
              }}
              value={generalInfo.ethnic_appearance_secondary}
              options={EthnicAppearances.map((i) => i.value)}
              getOptionSelected={(option, value) => option === value}
              fullWidth
              classes={{
                option: classes.option,
              }}
              renderInput={(params) => (
                <TextInput
                  placeholder={'Select'}
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    classes: {
                      root: classes.textInputStyle,
                    },
                  }}
                />
              )}
            />
          </div>
        )}
        {!compCardAdd && (
          <div>
            <InputLabel id="otherEthnicAppearance" style={{ color: '#98a1ac' }}>
              Ethnic Appearance (Other)
            </InputLabel>
            <Autocomplete
              onChange={(e, newValue) => {
                handleAutocompleteChange('ethnic_appearance_other', newValue);
              }}
              value={generalInfo.ethnic_appearance_other}
              options={EthnicAppearances.map((i) => i.value)}
              getOptionSelected={(option, value) => option === value}
              fullWidth
              classes={{
                option: classes.option,
              }}
              renderInput={(params) => (
                <TextInput
                  placeholder={'Select'}
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    classes: {
                      root: classes.textInputStyle,
                    },
                  }}
                />
              )}
            />
          </div>
        )}

        <div>
          <CompCardButton
            onClick={compCardAdd}
            attributes={{
              label: 'Eye Colour',
              key: 'eye_colour',
            }}
            hasValue={generalInfo.eye_colour !== '' && generalInfo.eye_colour !== null}
          />
          <Autocomplete
            onChange={(e, newValue) => {
              handleAutocompleteChange('eye_colour', newValue);
            }}
            value={generalInfo.eye_colour}
            options={eyeColour.map((i) => i.value)}
            getOptionSelected={(option, value) => option === value}
            fullWidth
            classes={{
              option: classes.option,
            }}
            renderInput={(params) => (
              <TextInput
                placeholder={'Select'}
                {...params}
                InputProps={{
                  ...params.InputProps,
                  classes: {
                    root: classes.textInputStyle,
                  },
                }}
              />
            )}
            disableClearable
          />
        </div>
        <div>
          <CompCardButton
            onClick={compCardAdd}
            attributes={{
              label: 'Hair Colour',
              key: 'hair_colour',
            }}
            hasValue={generalInfo.hair_colour !== '' && generalInfo.hair_colour !== null}
          />
          <Autocomplete
            onChange={(e, newValue) => {
              handleAutocompleteChange('hair_colour', newValue);
            }}
            value={generalInfo.hair_colour}
            options={hairColour.map((i) => i.value)}
            getOptionSelected={(option, value) => option === value}
            fullWidth
            classes={{
              option: classes.option,
            }}
            renderInput={(params) => (
              <TextInput
                placeholder={'Select'}
                {...params}
                InputProps={{
                  ...params.InputProps,
                  classes: {
                    root: classes.textInputStyle,
                  },
                }}
              />
            )}
            disableClearable
          />
        </div>
        <div>
          <CompCardButton
            onClick={compCardAdd}
            attributes={{
              label: 'Hair Length',
              key: 'hair_length',
            }}
            hasValue={generalInfo.hair_length !== '' && generalInfo.hair_length !== null}
          />
          <Autocomplete
            onChange={(e, newValue) => {
              handleAutocompleteChange('hair_length', newValue);
            }}
            value={generalInfo.hair_length}
            options={hairLength.map((i) => i.value)}
            getOptionSelected={(option, value) => option === value}
            fullWidth
            classes={{
              option: classes.option,
            }}
            renderInput={(params) => (
              <TextInput
                placeholder={'Select'}
                {...params}
                InputProps={{
                  ...params.InputProps,
                  classes: {
                    root: classes.textInputStyle,
                  },
                }}
              />
            )}
            disableClearable
          />
        </div>
      </ThreeColumn>
    </OpportunitiesCont>
  );
};

export default GeneralOnSizeCard;
